import React, { useEffect, useState, createElement } from 'react';
import { BookOpen, Check, ChevronDown, ChevronUp, Clock, Shield, Utensils, AlertCircle, Heart, Menu, ArrowRight, Star, Coffee, Activity, X } from 'lucide-react';
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// --- Color Palette Constants (for reference in Tailwind arbitrary values) ---
// Deep Teal: #1a4a5c
// Warm Sand: #d4b896
// Golden Olive: #8b7355
// Cream: #f5f0e8
// White: #ffffff
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  // Initialize Meta Pixel and fire PageView
  useEffect(() => {
    // Meta Pixel initialization
    if (!window.fbq) {
      const n = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      // Load the Facebook Pixel script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    // Initialize both Pixel IDs and fire PageView
    window.fbq('init', '1005182096007705');
    window.fbq('init', '1282671027240812');
    window.fbq('track', 'PageView');
  }, []);
  // Handle Add to Cart click with tracking
  const handleAddToCart = () => {
    // Fire AddToCart event
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Sin Vesícula, Sin Miedo - Guía Digital',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
    // Redirect to Shopify cart
    window.open('https://www.vidasimple365.com/cart/55228800237736:1', '_blank', 'noopener,noreferrer');
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen w-full bg-[#f5f0e8] font-sans text-slate-800 selection:bg-[#d4b896] selection:text-[#1a4a5c]" data-id="element-0">
      {/* --- HERO SECTION --- */}
      <header className="relative overflow-hidden pt-12 pb-20 lg:pt-24 lg:pb-32 px-4 sm:px-6 lg:px-8" data-id="element-1">
        <div className="max-w-7xl mx-auto" data-id="element-2">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-3">
            {/* Hero Content */}
            <div className="flex-1 text-center lg:text-left z-10" data-id="element-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a4a5c]/10 text-[#1a4a5c] font-medium text-sm mb-6" data-id="element-5">
                <Star className="w-4 h-4 fill-[#1a4a5c]" data-id="element-6" />
                <span data-id="element-7">Guía práctica + Menú de 14 días</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a4a5c] leading-tight mb-6 tracking-tight" data-id="element-8">
                Sin Vesícula, <br className="hidden lg:block" data-id="element-9" />
                <span className="text-[#8b7355]" data-id="element-10">Sin Miedo</span>
              </h1>

              <p className="text-xl md:text-2xl text-slate-600 mb-4 font-medium" data-id="element-11">
                El Plato Sin Vesícula (Método 3E): <br data-id="element-12" />
                <span className="text-[#1a4a5c]" data-id="element-13">
                  Elegir + Equilibrar + Enfriar
                </span>
              </p>

              <p className="text-lg text-slate-500 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-14">
                Deja de adivinar qué comer. Una guía práctica para recuperar tu
                tranquilidad digestiva y volver a disfrutar de la comida sin
                dolor ni urgencias.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-15">
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mt-2 sm:mt-0 px-4" data-id="element-16">
                  <Shield className="w-4 h-4 text-[#8b7355]" data-id="element-17" />
                  <span data-id="element-18">Compra segura</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="flex-1 relative w-full max-w-md lg:max-w-full" data-id="element-19">
              <div className="relative z-10 transform transition-transform hover:scale-[1.02] duration-500" data-id="element-20">
                <div className="absolute inset-0 bg-[#d4b896] rounded-3xl blur-2xl opacity-30 -rotate-6 scale-95" data-id="element-21"></div>
                <img src="https://cdn.magicpatterns.com/uploads/6kLwu7bpF5dQgcaZoz9Uxm/IMAGEN_SIN_VESICULA,_SIN_MIEDO.png" alt="Portada del libro Sin Vesícula, Sin Miedo" className="relative rounded-2xl shadow-2xl w-full h-auto border-4 border-white" data-id="element-22" />
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 md:bottom-10 md:-right-10 bg-white p-4 rounded-xl shadow-xl z-20 max-w-[200px] hidden md:block animate-bounce-slow" data-id="element-23">
                <div className="flex items-center gap-3 mb-2" data-id="element-24">
                  <div className="p-2 bg-green-100 rounded-full" data-id="element-25">
                    <Check className="w-5 h-5 text-green-600" data-id="element-26" />
                  </div>
                  <span className="font-bold text-[#1a4a5c]" data-id="element-27">Resultado</span>
                </div>
                <p className="text-sm text-slate-600 leading-snug" data-id="element-28">
                  "Por fin pude comer fuera sin buscar el baño corriendo."
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* --- PAIN POINTS SECTION --- */}
      <section className="py-20 bg-white" data-id="element-29">
        <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-30">
          <div className="text-center mb-16" data-id="element-31">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a4a5c] mb-4" data-id="element-32">
              ¿Te identificas con esto?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-33">
              La vida sin vesícula puede sentirse como un campo minado si no
              tienes el mapa correcto.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-34">
            {['Urgencia al baño inmediatamente después de comer', 'Diarrea o evacuaciones explosivas impredecibles', 'Gases e inflamación que duran horas', 'Miedo a comer fuera de casa o en eventos', 'Días buenos y días malos sin entender la causa'].map((pain, idx) => <div key={idx} className="flex items-start gap-4 p-6 bg-[#f5f0e8] rounded-xl border border-[#e6dacd]" data-id="element-35">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a4a5c]/10 flex items-center justify-center mt-0.5" data-id="element-36">
                  <AlertCircle className="w-5 h-5 text-[#1a4a5c]" data-id="element-37" />
                </div>
                <p className="text-slate-700 font-medium" data-id="element-38">{pain}</p>
              </div>)}
          </div>

          <div className="bg-[#1a4a5c] text-white p-8 rounded-2xl text-center shadow-lg" data-id="element-39">
            <p className="text-xl md:text-2xl font-medium mb-2" data-id="element-40">
              No es tu culpa.
            </p>
            <p className="text-[#d4b896] text-lg" data-id="element-41">
              Tu sistema digestivo ahora responde diferente, y nadie te enseñó
              las nuevas reglas... hasta ahora.
            </p>
          </div>
        </div>
      </section>

      {/* --- METHOD 3E SECTION --- */}
      <section className="py-24 bg-[#1a4a5c] text-white relative overflow-hidden" data-id="element-42">
        {/* Background Pattern */}
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none" data-id="element-43">
          <div className="absolute right-0 top-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-44"></div>
          <div className="absolute left-0 bottom-0 w-96 h-96 bg-[#d4b896] rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-45"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-46">
          <div className="text-center mb-20" data-id="element-47">
            <span className="text-[#d4b896] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-48">
              El Secreto
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-49">
              El Método 3E: Tu GPS para Comer
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto" data-id="element-50">
              No se trata de eliminar grasas para siempre. Se trata de gestionar
              el "golpe digestivo" con tres pasos simples.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-51">
            {/* E1 */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors" data-id="element-52">
              <div className="w-14 h-14 bg-[#d4b896] rounded-xl flex items-center justify-center mb-6 shadow-lg text-[#1a4a5c] font-bold text-2xl" data-id="element-53">
                E1
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[#d4b896]" data-id="element-54">Elegir</h3>
              <p className="text-slate-300 mb-6" data-id="element-55">
                Aprende a identificar tus gatillos personales. No todas las
                grasas son iguales, y tu tolerancia es única.
              </p>
              <ul className="space-y-3 text-sm text-slate-300" data-id="element-56">
                <li className="flex items-center gap-2" data-id="element-57">
                  <Check className="w-4 h-4 text-[#d4b896]" data-id="element-58" /> Tipo de grasa
                </li>
                <li className="flex items-center gap-2" data-id="element-59">
                  <Check className="w-4 h-4 text-[#d4b896]" data-id="element-60" /> Forma de cocción
                </li>
                <li className="flex items-center gap-2" data-id="element-61">
                  <Check className="w-4 h-4 text-[#d4b896]" data-id="element-62" /> Dosis personal
                </li>
              </ul>
            </div>

            {/* E2 */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 transform md:-translate-y-4 shadow-xl" data-id="element-63">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-lg text-[#1a4a5c] font-bold text-2xl" data-id="element-64">
                E2
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-65">Equilibrar</h3>
              <p className="text-slate-200 mb-6" data-id="element-66">
                La clave no es solo qué comes, sino con qué lo acompañas. Crea
                un "amortiguador" digestivo.
              </p>
              <ul className="space-y-3 text-sm text-slate-200" data-id="element-67">
                <li className="flex items-center gap-2" data-id="element-68">
                  <Check className="w-4 h-4 text-white" data-id="element-69" /> Proteína
                  estabilizadora
                </li>
                <li className="flex items-center gap-2" data-id="element-70">
                  <Check className="w-4 h-4 text-white" data-id="element-71" /> Fibra soluble
                </li>
                <li className="flex items-center gap-2" data-id="element-72">
                  <Check className="w-4 h-4 text-white" data-id="element-73" /> Volumen inteligente
                </li>
              </ul>
            </div>

            {/* E3 */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors" data-id="element-74">
              <div className="w-14 h-14 bg-[#8b7355] rounded-xl flex items-center justify-center mb-6 shadow-lg text-white font-bold text-2xl" data-id="element-75">
                E3
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[#d4b896]" data-id="element-76">
                Enfriar
              </h3>
              <p className="text-slate-300 mb-6" data-id="element-77">
                Baja la intensidad del impacto digestivo controlando factores
                que nadie te menciona.
              </p>
              <ul className="space-y-3 text-sm text-slate-300" data-id="element-78">
                <li className="flex items-center gap-2" data-id="element-79">
                  <Check className="w-4 h-4 text-[#8b7355]" data-id="element-80" /> Ritmo al comer
                </li>
                <li className="flex items-center gap-2" data-id="element-81">
                  <Check className="w-4 h-4 text-[#8b7355]" data-id="element-82" /> Orden del plato
                </li>
                <li className="flex items-center gap-2" data-id="element-83">
                  <Check className="w-4 h-4 text-[#8b7355]" data-id="element-84" /> Ventanas del día
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- WHAT'S INSIDE SECTION --- */}
      <section className="py-20 bg-[#f5f0e8]" data-id="element-85">
        <div className="max-w-6xl mx-auto px-4 sm:px-6" data-id="element-86">
          <div className="text-center mb-16" data-id="element-87">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a4a5c] mb-4" data-id="element-88">
              ¿Qué incluye la guía?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-89">
              Todo lo que necesitas para pasar de la confusión a la confianza.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-90">
            {[{
            icon: BookOpen,
            title: '12 Capítulos Prácticos',
            desc: 'Información directa, sin relleno médico innecesario.'
          }, {
            icon: Activity,
            title: 'Método 3E Paso a Paso',
            desc: 'La estrategia central para comer sin miedo.'
          }, {
            icon: Activity,
            title: 'Semáforo de Alimentos',
            desc: 'Clasificación Verde/Amarillo/Rojo para decisiones rápidas.'
          }, {
            icon: Utensils,
            title: '6+ Recetas 3E',
            desc: 'Platos reales, simples y seguros para tu digestión.'
          }, {
            icon: Menu,
            title: 'Menú de 14 Días',
            desc: 'Plan completo para estabilizarte y reintroducir alimentos.'
          }, {
            icon: Shield,
            title: 'Plan Anti-Crisis',
            desc: 'Protocolo de rescate para cuando algo te cae mal.'
          }, {
            icon: Coffee,
            title: 'Guía para Restaurantes',
            desc: 'Scripts exactos para pedir comida fuera de casa.'
          }, {
            icon: Check,
            title: 'Plantillas Personalizables',
            desc: 'Crea tu propio manual de instrucciones corporal.'
          }, {
            icon: Star,
            title: 'FAQ Detallado',
            desc: 'Respuestas a tus dudas sobre grasas, alcohol y más.'
          }].map((feature, idx) => <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-[#e6dacd] hover:shadow-md transition-shadow" data-id="element-91">
                <div className="w-10 h-10 bg-[#f5f0e8] rounded-lg flex items-center justify-center mb-4 text-[#1a4a5c]" data-id="element-92">
                  <feature.icon className="w-5 h-5" data-id="element-93" />
                </div>
                <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-94">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600" data-id="element-95">{feature.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* --- BENEFITS SECTION --- */}
      <section className="py-20 bg-white" data-id="element-96">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-97">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-98">
            <div className="flex-1 order-2 md:order-1" data-id="element-99">
              <div className="relative" data-id="element-100">
                <div className="absolute -inset-4 bg-[#d4b896]/20 rounded-full blur-3xl" data-id="element-101"></div>
                <img src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=800" alt="Comida saludable y equilibrada" className="relative rounded-2xl shadow-xl w-full object-cover h-[400px]" data-id="element-102" />
              </div>
            </div>
            <div className="flex-1 order-1 md:order-2" data-id="element-103">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a4a5c] mb-6" data-id="element-104">
                Lo que vas a lograr
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-105">
                Esta guía no está hecha para que "comas perfecto". Está hecha
                para que vuelvas a comer con tranquilidad.
              </p>

              <ul className="space-y-4" data-id="element-106">
                {['Menos urgencia al baño y visitas inesperadas', 'Reducción de gases e inflamación post-comida', 'Más control y confianza al comer en restaurantes', 'Más variedad en tu dieta sin pagar el precio después', 'Entender por fin qué le pasa a tu cuerpo', 'Tu propio manual personalizado de alimentación'].map((benefit, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-107">
                    <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-[#8b7355] flex items-center justify-center" data-id="element-108">
                      <Check className="w-3 h-3 text-white" data-id="element-109" />
                    </div>
                    <span className="text-slate-700 font-medium" data-id="element-110">
                      {benefit}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- HOW TO USE SECTION --- */}
      <section className="py-20 bg-[#1a4a5c] text-white" data-id="element-111">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-112">
          <h2 className="text-3xl md:text-4xl font-bold mb-12" data-id="element-113">
            Empieza en 15 minutos al día
          </h2>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-114">
            {/* Connector Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-white/20 -z-0" data-id="element-115"></div>

            {[{
            step: '1',
            title: 'Lee',
            desc: 'Lee el Capítulo 3 (Resumen del Método 3E) para entender las bases.'
          }, {
            step: '2',
            title: 'Aplica',
            desc: 'Usa el 3E en solo 2 comidas al día durante 7 días.'
          }, {
            step: '3',
            title: 'Expande',
            desc: 'Usa el Menú de 14 días y ajusta con tu Semáforo personal.'
          }].map((item, idx) => <div key={idx} className="relative z-10 flex flex-col items-center" data-id="element-116">
                <div className="w-24 h-24 bg-[#d4b896] rounded-full flex items-center justify-center text-4xl font-bold text-[#1a4a5c] mb-6 shadow-lg border-4 border-[#1a4a5c]" data-id="element-117">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-118">{item.title}</h3>
                <p className="text-slate-300 text-sm px-4" data-id="element-119">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* --- PRICING SECTION --- */}
      <section id="pricing" className="py-24 bg-[#f5f0e8]" data-id="element-120">
        <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-121">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-[#d4b896]/30" data-id="element-122">
            <div className="bg-[#1a4a5c] p-8 text-center text-white" data-id="element-123">
              <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-124">
                Recupera la tranquilidad de comer
              </h2>
              <p className="text-[#d4b896] font-medium" data-id="element-125">
                Oferta Especial de Lanzamiento
              </p>
            </div>

            <div className="p-8 md:p-12" data-id="element-126">
              <div className="flex flex-col md:flex-row justify-between items-center mb-10 border-b border-slate-100 pb-8" data-id="element-127">
                <div data-id="element-128">
                  <div className="flex items-baseline gap-3 mb-2" data-id="element-129">
                    <span className="text-2xl text-slate-400 line-through font-medium" data-id="element-130">
                      $27
                    </span>
                    <h3 className="text-4xl font-bold text-[#1a4a5c]" data-id="element-131">
                      $6.39{' '}
                      <span className="text-xl text-slate-500 font-normal" data-id="element-132">
                        USD
                      </span>
                    </h3>
                  </div>
                  <p className="text-slate-500 text-sm" data-id="element-133">
                    Pago único. Acceso inmediato.
                  </p>
                </div>
                <div className="mt-6 md:mt-0" data-id="element-134">
                  <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium" data-id="element-135">
                    <Check className="w-4 h-4" data-id="element-136" />
                    Descarga Digital PDF
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-10" data-id="element-137">
                <h4 className="font-bold text-slate-800 mb-4" data-id="element-138">
                  Todo lo que recibes hoy:
                </h4>
                {["Ebook Completo 'Sin Vesícula, Sin Miedo'", 'El Método 3E explicado paso a paso', 'Menú detallado de 14 días', 'Guía de bolsillo para restaurantes', 'Protocolo de emergencia anti-crisis', 'Plantillas de seguimiento personal'].map((item, idx) => <div key={idx} className="flex items-center gap-3" data-id="element-139">
                    <div className="w-6 h-6 rounded-full bg-[#f5f0e8] flex items-center justify-center flex-shrink-0" data-id="element-140">
                      <Check className="w-4 h-4 text-[#1a4a5c]" data-id="element-141" />
                    </div>
                    <span className="text-slate-700" data-id="element-142">{item}</span>
                  </div>)}
              </div>

              <button onClick={handleAddToCart} className="w-full py-4 text-xl font-bold text-white bg-[#8b7355] rounded-xl hover:bg-[#7a654a] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 mb-6 flex items-center justify-center gap-2" data-id="element-143">
                Quiero Mi Guía Ahora
                <ArrowRight className="w-6 h-6" data-id="element-144" />
              </button>

              <p className="text-center text-xs text-slate-400" data-id="element-145">
                Formato PDF digital. Compatible con todos los dispositivos.
                <br data-id="element-146" />
                Entrega inmediata a tu correo electrónico.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="py-20 bg-white" data-id="element-147">
        <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-148">
          <h2 className="text-3xl font-bold text-[#1a4a5c] text-center mb-12" data-id="element-149">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-4" data-id="element-150">
            {[{
            q: '¿Nunca volveré a comer grasa?',
            a: "No necesariamente. El objetivo es encontrar tu 'umbral' personal y aprender a comer grasa de forma equilibrada usando el método 3E para que no te caiga mal."
          }, {
            q: '¿Por qué a veces me cae bien algo y a veces no?',
            a: "Porque cambia el 'golpe digestivo'. Depende de la porción, la velocidad al comer, tu nivel de estrés, con qué mezclaste el alimento y hasta la hora del día."
          }, {
            q: '¿Esto sirve si tengo diarrea frecuente?',
            a: 'Puede ayudar significativamente a reducir episodios mediante el manejo estratégico de las comidas. Sin embargo, si la diarrea es severa, persistente o hay otros síntomas de alarma, necesitas evaluación médica.'
          }, {
            q: '¿Necesito seguir el menú exactamente?',
            a: 'No. El menú de 14 días es una guía estructurada para facilitarte la vida, pero el objetivo es que aprendas el sistema para aplicarlo a cualquier comida que te guste.'
          }].map((faq, idx) => <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-151">
                <button onClick={() => toggleFaq(idx)} className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-slate-50 transition-colors" data-id="element-152">
                  <span className="font-bold text-[#1a4a5c] text-lg pr-8" data-id="element-153">
                    {faq.q}
                  </span>
                  {openFaq === idx ? <ChevronUp className="w-5 h-5 text-[#8b7355] flex-shrink-0" data-id="element-154" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-155" />}
                </button>
                {openFaq === idx && <div className="p-6 pt-0 bg-slate-50 text-slate-600 leading-relaxed border-t border-slate-100" data-id="element-156">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* --- DISCLAIMER & FOOTER --- */}
      <footer className="bg-[#1a4a5c] text-slate-400 py-12 border-t border-white/10" data-id="element-157">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-158">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-159">
            <div className="flex items-center justify-center gap-2 mb-4 text-[#d4b896]" data-id="element-160">
              <AlertCircle className="w-5 h-5" data-id="element-161" />
              <span className="font-bold uppercase text-xs tracking-wider" data-id="element-162">
                Aviso Importante
              </span>
            </div>
            <p className="text-sm leading-relaxed text-slate-300 opacity-80" data-id="element-163">
              Este material es educativo y no sustituye evaluación médica. Si
              tienes fiebre, ictericia (piel/ojos amarillos), dolor intenso
              persistente, sangre en heces, pérdida de peso no intencional,
              deshidratación o síntomas que empeoran rápido, busca atención
              médica inmediata.
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/10 text-sm" data-id="element-164">
            <p data-id="element-165">
              &copy; {new Date().getFullYear()} Sin Vesícula, Sin Miedo. Todos
              los derechos reservados.
            </p>
            <div className="flex gap-6 mt-4 md:mt-0" data-id="element-166">
              <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors" data-id="element-167">
                Términos
              </button>
              <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors" data-id="element-168">
                Privacidad
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* --- LEGAL MODALS --- */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-169">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-170">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-[#f5f0e8]" data-id="element-171">
              <h2 className="text-xl font-bold text-[#1a4a5c]" data-id="element-172">
                {activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-[#1a4a5c]/10 transition-colors text-[#1a4a5c]" data-id="element-173">
                <X className="w-5 h-5" data-id="element-174" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] text-slate-700" data-id="element-175">
              {activeModal === 'terms' ? <div className="space-y-6" data-id="element-176">
                  <section data-id="element-177">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-178">
                      1. Aceptación de los Términos
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-179">
                      Al adquirir y utilizar la guía "Sin Vesícula, Sin Miedo",
                      aceptas estos términos y condiciones en su totalidad. Si
                      no estás de acuerdo con alguna parte de estos términos, te
                      recomendamos no realizar la compra.
                    </p>
                  </section>

                  <section data-id="element-180">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-181">
                      2. Naturaleza del Producto
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-182">
                      Este producto es un material educativo en formato digital
                      (PDF). El contenido está diseñado para proporcionar
                      información general sobre alimentación
                      post-colecistectomía y no constituye asesoramiento médico
                      profesional.
                    </p>
                  </section>

                  <section data-id="element-183">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-184">
                      3. Uso del Contenido
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-185">
                      El contenido de esta guía es para uso personal únicamente.
                      Queda prohibida la reproducción, distribución,
                      modificación o uso comercial del material sin autorización
                      expresa por escrito.
                    </p>
                  </section>

                  <section data-id="element-186">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-187">
                      4. Descargo de Responsabilidad Médica
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-188">
                      La información proporcionada no sustituye la consulta
                      médica profesional. Siempre consulta con tu médico antes
                      de realizar cambios significativos en tu dieta,
                      especialmente si tienes condiciones de salud
                      preexistentes.
                    </p>
                  </section>

                  <section data-id="element-189">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-190">
                      5. Política de Reembolso
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-191">
                      Debido a la naturaleza digital del producto, no se ofrecen
                      reembolsos una vez que el archivo ha sido descargado. Te
                      recomendamos revisar toda la información disponible antes
                      de realizar tu compra.
                    </p>
                  </section>

                  <section data-id="element-192">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-193">
                      6. Modificaciones
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-194">
                      Nos reservamos el derecho de modificar estos términos en
                      cualquier momento. Las modificaciones entrarán en vigor
                      inmediatamente después de su publicación.
                    </p>
                  </section>
                </div> : <div className="space-y-6" data-id="element-195">
                  <section data-id="element-196">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-197">
                      1. Información que Recopilamos
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-198">
                      Recopilamos únicamente la información necesaria para
                      procesar tu compra: nombre, dirección de correo
                      electrónico e información de pago. No almacenamos datos de
                      tarjetas de crédito directamente.
                    </p>
                  </section>

                  <section data-id="element-199">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-200">
                      2. Uso de la Información
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-201">
                      Tu información se utiliza exclusivamente para: procesar y
                      entregar tu compra, enviarte el producto digital
                      adquirido, y comunicarnos contigo sobre tu pedido si es
                      necesario.
                    </p>
                  </section>

                  <section data-id="element-202">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-203">
                      3. Protección de Datos
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-204">
                      Implementamos medidas de seguridad técnicas y
                      organizativas para proteger tu información personal contra
                      acceso no autorizado, alteración, divulgación o
                      destrucción.
                    </p>
                  </section>

                  <section data-id="element-205">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-206">
                      4. Compartir Información
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-207">
                      No vendemos, comercializamos ni transferimos tu
                      información personal a terceros. Solo compartimos datos
                      con proveedores de servicios de pago necesarios para
                      procesar tu transacción.
                    </p>
                  </section>

                  <section data-id="element-208">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-209">
                      5. Cookies
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-210">
                      Utilizamos cookies esenciales para el funcionamiento del
                      sitio web. Estas cookies no recopilan información personal
                      identificable y son necesarias para brindarte una
                      experiencia de compra segura.
                    </p>
                  </section>

                  <section data-id="element-211">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-212">
                      6. Tus Derechos
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-213">
                      Tienes derecho a acceder, rectificar o eliminar tu
                      información personal. Para ejercer estos derechos, puedes
                      contactarnos a través de los canales oficiales de
                      comunicación.
                    </p>
                  </section>

                  <section data-id="element-214">
                    <h3 className="font-bold text-[#1a4a5c] mb-2" data-id="element-215">
                      7. Cambios en la Política
                    </h3>
                    <p className="text-sm leading-relaxed" data-id="element-216">
                      Podemos actualizar esta política de privacidad
                      ocasionalmente. Te notificaremos sobre cambios
                      significativos publicando la nueva política en esta
                      página.
                    </p>
                  </section>
                </div>}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50" data-id="element-217">
              <button onClick={() => setActiveModal(null)} className="w-full py-3 text-sm font-medium text-white bg-[#1a4a5c] rounded-lg hover:bg-[#153d4c] transition-colors" data-id="element-218">
                Cerrar
              </button>
            </div>
          </div>
        </div>}
    </div>;
}